// eslint-disable-next-line import/no-unresolved -- This import exists only at runtime (VITE Magic for the Daffy Integration)
import 'vite/modulepreload-polyfill'; // REQUIRED FIRST while SPA is served via Daffy Django BE

import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
// See the comment above the 'dayjs.extend' statements for more on this
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import log from '~/helpers/logging';

import Dashboard from '~/dashboard';
import {
  initializePage,
  initializeSupportWidget,
} from '~/helpers/initialization';

import AppProvider from './AppProvider';

// Required CSS -- Loaded early for bundle splitting and so that ComponentLib Styles can take higher priority in later renders
import 'css/dashboard.scss';
import 'css/admin.scss';
import 'css/applications.scss';
import 'css/approvals.scss';
import 'css/audits.scss';
import 'css/entities.scss';
import 'css/evaluations.scss';
import 'css/flow.scss';
import 'css/journey-analytics.scss';
import 'css/notifications.scss';
import 'css/settings.scss';

// VENDOR CSS
import 'react-flow-renderer/dist/style.css';
import '~/journey/react-flow-renderer-upgrade/styles.css';
import '@usealloy/component-library/build/style.css';

/* The Component Library's DateRangePicker uses antd's RangePicker component
 * which comes from the antd's DatePicker component. The DatePicker component
 * uses another 3rd party component: rc-picker. rc-picker is what uses the dayjs
 * plugins. Daffy's files don't explicitly use them so Webpack omits them while
 * bundling and for some reason components from the Component Library looks at
 * Daffy's node_modules instead of its own.
 * This is a bandaid solution until we can figure out how to resolve the
 * node_modules.
 */
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

document.addEventListener('DOMContentLoaded', async () => {
  const {
    alloyEnvironment,
    isAuthenticated,
    isVanityUrlLogin,
    redirectToAlloyCom,
    cellBaseAppUrls,
    isEphemeralEnvironment,
  } = await initializePage(true);

  let userContext = null;
  if (isAuthenticated) {
    try {
      userContext = await ajaxFetch(routes.v3.userContext, { camelCase: true });
      initializeSupportWidget({
        connectorJwt: userContext?.intercom?.connectorJwt,
        connectorJwtTokenKey: userContext?.intercom?.connectorJwtTokenKey,
        userJwt: userContext?.intercom?.userJwt,
      });
    } catch (err) {
      log(
        'error',
        'Failed to fetch feature flags in App.jsx, rendering dashboard without concurrency',
        {},
        err,
      );
      initializeSupportWidget();
    }
  } else {
    initializeSupportWidget();
  }
  const featureFlags = userContext?.featureFlags || userContext?.feature_flags || {};

  const hasVanityRedirectParam = new URLSearchParams(window.location.search).get('vanityDomainRedirect')
    === 'true';

  if (
    featureFlags['react-18Concurrency']
    && (!isVanityUrlLogin || (isVanityUrlLogin && hasVanityRedirectParam))
  ) {
    const container = document.getElementById('root');
    if (!container) {
      // eslint-disable-next-line no-console
      console.error('React root element “#root” not found – aborting mount');
      return;
    }
    const root = createRoot(container);
    root.render(
      <AppProvider
        alloyEnvironment={alloyEnvironment}
        isAuthenticated={isAuthenticated}
        initialUserContext={userContext}
      >
        <Dashboard
          redirectToAlloyCom={redirectToAlloyCom}
          cellBaseAppUrls={cellBaseAppUrls}
          isEphemeralEnvironment={isEphemeralEnvironment}
        />
      </AppProvider>,
    );
  } else if (
    !isVanityUrlLogin
    || (isVanityUrlLogin && hasVanityRedirectParam)
  ) {
    ReactDOM.render(
      <AppProvider
        alloyEnvironment={alloyEnvironment}
        isAuthenticated={isAuthenticated}
        initialUserContext={userContext}
      >
        <Dashboard
          redirectToAlloyCom={redirectToAlloyCom}
          cellBaseAppUrls={cellBaseAppUrls}
          isEphemeralEnvironment={isEphemeralEnvironment}
        />
      </AppProvider>,
      document.getElementById('root'),
    );
  }
});
